/**
 * SettingsTab — App settings organized into 4 clean, consumer-focused categories:
 * - Device & Video (Camera, resolution, virtual camera guide)
 * - Cloud GPU & Session (Dynamic RTX 5090 Blackwell telemetry, health, session wipe)
 * - License & Account (Cryptographic license, remaining minutes, support portal)
 * - About & Support (Version, privacy guarantee, terms)
 *
 * Developer TMI (Akool, Replicate, .env, engine_config.json) completely eliminated.
 */

import { useState } from "react";
import { DeviceSettings } from "../settings/DeviceSettings";
import { CloudNodeSettings } from "../settings/CloudNodeSettings";
import { LicenseSettings } from "../settings/LicenseSettings";
import { AboutSettings } from "../settings/AboutSettings";

type SettingsCategory = "device" | "cloud" | "license" | "about";

const CATEGORIES: { id: SettingsCategory; label: string; icon: string }[] = [
  { id: "device",  label: "Device & Video",      icon: "⊙" },
  { id: "cloud",   label: "Cloud GPU & Session", icon: "◈" },
  { id: "license", label: "License & Account",   icon: "◆" },
  { id: "about",   label: "About & Support",     icon: "ⓘ" },
];

interface SettingsTabProps {
  isMobile?: boolean;
}

function renderSettingsContent(active: SettingsCategory) {
  switch (active) {
    case "device":
      return <DeviceSettings />;
    case "cloud":
      return <CloudNodeSettings />;
    case "license":
      return <LicenseSettings />;
    case "about":
      return <AboutSettings />;
    default:
      return null;
  }
}

export function SettingsTab({ isMobile = false }: SettingsTabProps) {
  const [active, setActive] = useState<SettingsCategory>("device");

  if (isMobile) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Horizontal scrollable pill bar */}
        <div
          role="tablist"
          style={{
            display: "flex",
            overflowX: "auto",
            gap: "var(--space-2)",
            padding: "var(--space-3)",
            background: "var(--sidebar-bg)",
            borderBottom: "1px solid var(--border-subtle)",
            flexShrink: 0,
            scrollbarWidth: "none",
          }}
        >
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              role="tab"
              id={`${cat.id}-tab`}
              aria-selected={active === cat.id}
              aria-controls={`${cat.id}-panel`}
              tabIndex={active === cat.id ? 0 : -1}
              onClick={() => setActive(cat.id)}
              style={{
                flexShrink: 0,
                height: 36,
                display: "flex", alignItems: "center", gap: "var(--space-2)",
                padding: "0 var(--space-3)",
                borderRadius: "var(--radius-full)", border: "none",
                background: active === cat.id ? "var(--accent)" : "var(--bg-floating)",
                color: active === cat.id ? "#000" : "var(--text-secondary)",
                cursor: "pointer",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-sm)",
                fontWeight: active === cat.id ? "var(--weight-semibold)" : "var(--weight-normal)",
                whiteSpace: "nowrap",
                transition: "all var(--duration-fast)",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <span style={{ fontSize: 14, opacity: 0.7 }}>{cat.icon}</span>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Settings content */}
        <div
          role="tabpanel"
          id={`${active}-panel`}
          aria-labelledby={`${active}-tab`}
          style={{
            flex: 1, overflowY: "auto",
            padding: "var(--space-4)",
          }}
        >
          {renderSettingsContent(active)}
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex" }}>
      {/* Settings sidebar */}
      <div style={{
        width: 220, flexShrink: 0,
        background: "var(--sidebar-bg)",
        borderRight: "1px solid var(--border-subtle)",
        padding: "var(--space-4) var(--space-3)",
      }}>
        <div style={{
          fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)",
          color: "var(--text-muted)", letterSpacing: "0.06em",
          textTransform: "uppercase", marginBottom: "var(--space-3)",
          padding: "0 var(--space-2)",
        }}>
          Settings
        </div>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActive(cat.id)}
            style={{
              width: "100%", height: 34,
              display: "flex", alignItems: "center", gap: "var(--space-3)",
              padding: "0 var(--space-3)",
              borderRadius: "var(--radius-md)", border: "none",
              background: active === cat.id ? "var(--sidebar-active)" : "transparent",
              color: active === cat.id ? "var(--text-primary)" : "var(--text-secondary)",
              cursor: "pointer", textAlign: "left" as const,
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-sm)",
              fontWeight: active === cat.id ? "var(--weight-semibold)" : "var(--weight-normal)",
              marginBottom: 2,
              transition: "all var(--duration-fast)",
            }}
          >
            <span style={{ fontSize: 14, opacity: 0.7 }}>{cat.icon}</span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Settings content */}
      <div style={{
        flex: 1, overflowY: "auto",
        padding: "var(--space-8)",
        maxWidth: 600,
      }}>
        {renderSettingsContent(active)}
      </div>
    </div>
  );
}
